import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Grid,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import React, { useState } from 'react';
import { AuthContext, AuthData } from '../context/auth';
import { ConfigContext, ConfigData } from '../context/config';
import { CurrencyCode } from '../types/currencyCode';
import { Payment } from '../types/payments';
import { formatCurrency } from '../util/util';
import { StyledEmptyMessage } from './common';
import DefaultDatePicker from './form/DefaultDatePicker';
import PaymentDetails from './PaymentDetails';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	payments: Payment[];
}

const PaymentAccordions = (props: Props) => {
	const { payments } = props;

	const [expanded, setExpanded] = useState([] as string[]);

	const handleChange = (panel: any) => (event: any, isExpanded: boolean) => {
		const expandedAccordions = [...expanded];
		if (isExpanded) {
			expandedAccordions.push(panel);
		} else {
			const i = expanded.indexOf(panel);
			expandedAccordions.splice(i, 1);
		}
		setExpanded(expandedAccordions);
	};

	if (!payments.length) {
		return null;
	}

	const noPaymentsMessage = () => {
		return <StyledEmptyMessage>No Payments</StyledEmptyMessage>;
	};

	return (
		<>
			<div style={{ margin: '16px 0' }}>Payments</div>
			{payments && payments.length > 0
				? payments.map((p: Payment) => {
						const title = (
							<Typography sx={{ width: '50%' }}>
								Payment {p.displayId}
								<Tooltip
									title='Copy payment locator to clipboard'
									arrow
									placement='top'
								>
									<IconButton
										color='info'
										aria-label='copy payment locator to clipboard'
										onClick={() => navigator.clipboard.writeText(p.locator)}
										size='small'
										sx={{ marginLeft: '6px', marginBottom: '2px' }}
									>
										<ContentCopyIcon sx={{ fontSize: 18 }} color='primary' />
									</IconButton>
								</Tooltip>
							</Typography>
						);
						const subTitle = p.reversedTimestamp ? (
							<Typography>Reversed</Typography>
						) : null;
						return (
							<Accordion
								key={p.displayId}
								expanded={expanded.includes(p.displayId)}
								onChange={handleChange(p.displayId)}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									id={p.displayId}
								>
									{title}
									{subTitle}
								</AccordionSummary>
								<AccordionDetails sx={{ pb: 2 }}>
									<Grid container spacing={2} sx={{ pb: 2 }}>
										<Grid item xs={4}>
											<DefaultDatePicker
												value={new Date(parseInt(p.postedTimestamp, 10))}
												onChange={null}
												label='Payment Date'
												style={{
													width: '100%',
													marginTop: '16px',
													marginRight: '4%',
												}}
												disabled
												showCalendar
												id={`${p.displayId}-payment-date`}
											/>
										</Grid>
										<Grid item xs={4}>
											<TextField
												id={`${p.displayId}-payment-amount`}
												sx={{ width: '100%', mt: '16px', mr: '4%' }}
												label='Payment Amount'
												value={formatCurrency(
													parseFloat(p.amount),
													p.amountCurrency as CurrencyCode,
												)}
												disabled
											/>
										</Grid>
										{p.reversedTimestamp ? (
											<Grid item xs={4}>
												<DefaultDatePicker
													value={new Date(parseInt(p.reversedTimestamp, 10))}
													onChange={null}
													label='Reversed Date'
													style={{ width: '100%', marginTop: '16px' }}
													disabled
													showCalendar
													id={`${p.displayId}-reversed-date`}
												/>
											</Grid>
										) : null}
									</Grid>
									<Grid container spacing={3}>
										<PaymentDetails payment={p} />
									</Grid>
								</AccordionDetails>
							</Accordion>
						);
				  })
				: noPaymentsMessage()}
		</>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<Component {...props} authData={authData} configData={configData} />
					)}
				</ConfigContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(PaymentAccordions);
